import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import SuperuserLogin from './components/SuperuserLogin';
import TenantSelector from './components/TenantSelector';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import DeviceList from './components/DeviceList';
import DeviceControl from './components/DeviceControl';
import DataVisualization from './components/DataVisualization';
import Surveillance from './components/Surveillance';
import Zones from './components/Zones';
import HeatPump from './components/HeatPump';
import Users from './components/Users';
import Gateways from './components/Gateways';
import Shelly from './components/Shelly';
import NetworkTopology from './components/NetworkTopology';
import GuidePage from './components/GuidePage';
import DeviationPage from './components/DeviationPage';
import Inspections from './components/Inspections';
import Contacts from './components/Contacts';
import InspectionPage from './components/InspectionPage';
import RoundPage from './components/RoundPage';
import OffhoursPage from './components/OffhoursPage';
import Kiosk from './components/Kiosk';
import KioskAdmin from './components/KioskAdmin';
import Setpoints from './components/Setpoints';
import Billing from './components/Billing';
import DeviceSchedules from './components/DeviceSchedules';
import QrPage from './components/QrPage';
import QrPoints from './components/QrPoints';

/** Redirects unauthenticated users to /login. Redirects users with insufficient level to /. */
const PrivateRoute: React.FC<{ children: React.ReactElement; minLevel?: number }> = ({ children, minLevel = 1 }) => {
    const { token, user, isHandoffPending } = useAuth();
    const location = useLocation();
    if (isHandoffPending) return null;
    if (!token) return <Navigate to="/login" state={{ from: location }} replace />;
    const level = user?.userLevel === 'superuser' ? 3 : user?.userLevel === 'admin' ? 2 : 1;
    if (level < minLevel) return <Navigate to="/" replace />;
    return <Layout>{children}</Layout>;
};

const AppRoutes: React.FC = () => (
    <Routes>
        <Route path="/login"            element={<Login />} />
        <Route path="/superuser-login"  element={<SuperuserLogin />} />
        <Route path="/tenant-selector"  element={<TenantSelector />} />
        <Route path="/g/:token"         element={<GuidePage />} />
        <Route path="/deviation/:token" element={<DeviationPage />} />
        <Route path="/offhours/:token"  element={<OffhoursPage />} />
        <Route path="/inspection/:token" element={<InspectionPage />} />
        <Route path="/round/:token"      element={<RoundPage />} />
        <Route path="/qr/:token"         element={<QrPage />} />
        <Route path="/"                   element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/devices"            element={<PrivateRoute><DeviceList /></PrivateRoute>} />
        <Route path="/device-control"     element={<PrivateRoute><DeviceControl /></PrivateRoute>} />
        <Route path="/device-schedules"   element={<PrivateRoute><DeviceSchedules /></PrivateRoute>} />
        <Route path="/data-visualization" element={<PrivateRoute><DataVisualization /></PrivateRoute>} />
        <Route path="/surveillance"       element={<PrivateRoute><Surveillance /></PrivateRoute>} />
        <Route path="/inspections"        element={<PrivateRoute><Inspections /></PrivateRoute>} />
        <Route path="/qr-points"          element={<PrivateRoute minLevel={2}><QrPoints /></PrivateRoute>} />
        <Route path="/contacts"           element={<PrivateRoute minLevel={2}><Contacts /></PrivateRoute>} />
        <Route path="/setpoints"          element={<PrivateRoute minLevel={2}><Setpoints /></PrivateRoute>} />
        <Route path="/zones"              element={<PrivateRoute minLevel={2}><Zones /></PrivateRoute>} />
        <Route path="/heat-pump"          element={<PrivateRoute minLevel={2}><HeatPump /></PrivateRoute>} />
        <Route path="/users"              element={<PrivateRoute minLevel={2}><Users /></PrivateRoute>} />
        <Route path="/gateways"           element={<PrivateRoute minLevel={3}><Gateways /></PrivateRoute>} />
        <Route path="/billing"            element={<PrivateRoute minLevel={3}><Billing /></PrivateRoute>} />
        <Route path="/shelly"             element={<PrivateRoute minLevel={2}><Shelly /></PrivateRoute>} />
        <Route path="/network-topology"   element={<PrivateRoute minLevel={2}><NetworkTopology /></PrivateRoute>} />
        <Route path="/kiosk-admin"         element={<PrivateRoute minLevel={2}><KioskAdmin /></PrivateRoute>} />
        <Route path="/kiosk"              element={<Kiosk />} />
        <Route path="*"                   element={<Navigate to="/" replace />} />
    </Routes>
);

const App: React.FC = () => (
    <AuthProvider>
        <Router>
            <AppRoutes />
        </Router>
    </AuthProvider>
);

export default App;